import { Accordion, Alert, Anchor, Badge, Button, Code, Group, Stack, Table, Text, Title } from '@mantine/core';
import { Link } from 'react-router-dom';
import { useSuiteReadiness } from '@/hooks/api/automationSuites/useAutomationSuites.ts';
import { SuiteError } from '@/pages/AutomationSuites/SuiteShared.tsx';
import type { AutomationDocumentationReviewFreshness } from '@/types/automations.ts';
import type { SuiteDetail } from '@/types/automationSuites.ts';

const freshnessPresentation = {
  current_review: { label: 'Current review', color: 'green' },
  stale_review: { label: 'Review is stale', color: 'yellow' },
  no_review: { label: 'No recorded review', color: 'gray' },
  unknown: { label: 'Review freshness unknown', color: 'gray' },
  access_limited: { label: 'Review evidence access-limited', color: 'orange' },
} as const;

function Freshness({
  automationId,
  value,
}: {
  automationId: string;
  value: AutomationDocumentationReviewFreshness | null;
}) {
  if (!value) return <Text size="sm" c="dimmed">No observed head</Text>;
  const presentation = freshnessPresentation[value.state];
  const targetBlueprintHref = `/automations/${automationId}/editor?blueprintVersion=${encodeURIComponent(value.target_version_id)}`;
  const comparisonHref = value.comparison
    ? `/automations/${automationId}/editor?comparisonBaseVersion=${encodeURIComponent(value.comparison.base_version_id)}&comparisonTargetVersion=${encodeURIComponent(value.comparison.target_version_id)}`
    : null;
  return (
    <Stack gap={2}>
      <Badge color={presentation.color} variant="light" w="fit-content">
        {presentation.label}
      </Badge>
      {value.source_review && (
        <Text size="xs" c="dimmed">
          {value.source_review.relationship === 'direct' ? 'Reviewed on this version' : 'Inherited from'}{' '}
          <Anchor
            component={Link}
            to={`/automations/${automationId}/editor?blueprintVersion=${encodeURIComponent(value.source_review.automation_version_id)}`}
          >
            <Code>{value.source_review.automation_version_id}</Code>
          </Anchor>
        </Text>
      )}
      <Anchor component={Link} size="xs" to={targetBlueprintHref}>
        View target Blueprint
      </Anchor>
      {comparisonHref && (
        <Anchor component={Link} size="xs" to={comparisonHref}>
          Compare reviewed and target versions
        </Anchor>
      )}
      {!!value.reason_codes.length && (
        <Text size="xs" c="dimmed">
          {value.reason_codes.map((reason) => reason.replaceAll('_', ' ')).join(' · ')}
        </Text>
      )}
      {!!value.changed_boundaries.length && (
        <Text size="xs" c="dimmed">
          Changed: {value.changed_boundaries
            .flatMap((boundary) => boundary.facets.length ? boundary.facets : [boundary.kind])
            .map((facet) => facet.replaceAll('_', ' '))
            .toSorted()
            .join(', ')}
          {value.changed_boundaries_truncated ? ' (additional changes omitted)' : ''}
        </Text>
      )}
      <Accordion variant="contained">
        <Accordion.Item value="evidence">
          <Accordion.Control>Evidence details</Accordion.Control>
          <Accordion.Panel>
            <Stack gap={2}>
              <Text size="xs" c="dimmed">
                Target version: <Code>{value.target_version_id}</Code>
              </Text>
              <Text size="xs" c="dimmed">
                Changed boundaries observed: {value.changed_boundary_count}
              </Text>
              {value.limitations.map((limitation) => (
                <Text size="xs" c="dimmed" key={limitation}>
                  {limitation}
                </Text>
              ))}
            </Stack>
          </Accordion.Panel>
        </Accordion.Item>
      </Accordion>
    </Stack>
  );
}

export default function SuiteReadiness({ suite }: { suite: SuiteDetail }) {
  const readiness = useSuiteReadiness(suite.id, true);
  if (!readiness.isError && !readiness.isFetchedAfterMount) return <Text>Loading Suite readiness…</Text>;
  if (readiness.isError) return <SuiteError error={readiness.error} retry={() => void readiness.refetch()} />;
  if (!readiness.data) return null;
  const data = readiness.data;
  const documentationDebt = data.documentation_debt ?? {
    status: 'unknown',
    limitations: ['Documentation-debt evidence is unavailable; do not infer a clean result.'],
  };
  const observationTitle =
    data.observation_status === 'complete'
      ? 'Observed readiness'
      : data.observation_status === 'access_limited'
        ? 'Access-limited observation'
        : 'Incomplete observation';
  return (
    <Stack>
      <Title order={2}>Readiness</Title>
      <Alert
        color={data.observation_status === 'complete' ? 'blue' : 'orange'}
        title={observationTitle}
      >
        This is an advisory view of member evidence. It does not approve publication, establish runtime
        behavior, or prove customer acceptance.
      </Alert>
      <Group>
        <Button variant="default" disabled={readiness.isFetching} onClick={() => void readiness.refetch()}>
          Refresh readiness
        </Button>
        <Text size="sm" c="dimmed">
          Documentation debt: {documentationDebt.status.replaceAll('_', ' ')}
        </Text>
      </Group>
      <Table.ScrollContainer minWidth={860}>
        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Automation</Table.Th>
              <Table.Th>Latest head review</Table.Th>
              <Table.Th>Published head review</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {data.members.map((member) => (
              <Table.Tr key={member.automation_id}>
                <Table.Td>
                  {member.availability === 'deleted' ? (
                    <Text component="span">{member.name}</Text>
                  ) : (
                    <Anchor component={Link} to={`/automations/${member.automation_id}`}>
                      {member.name}
                    </Anchor>
                  )}
                  <Text size="xs" c="dimmed">{member.purpose ?? 'Purpose unavailable in this scope'}</Text>
                </Table.Td>
                <Table.Td>
                  <Freshness
                    automationId={member.automation_id}
                    value={member.review_freshness?.latest_head ?? null}
                  />
                </Table.Td>
                <Table.Td>
                  <Freshness
                    automationId={member.automation_id}
                    value={member.review_freshness?.published_head ?? null}
                  />
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
      {data.limitations.map((limitation) => (
        <Text size="xs" c="dimmed" key={limitation}>{limitation}</Text>
      ))}
    </Stack>
  );
}
